/**
 * Cross-Tool Navigation Utilities
 * Build URLs to navigate between tools with state transfer
 */

import { encodeState, getSearchParams, type URLStateObject } from './url-state';

// Tool path mappings - use canonical category-prefixed URLs
export const TOOL_PATHS = {
  // Planning tools
  'budget-calculator': '/tools/planning/cybersecurity-budget-calculator',
  'breach-cost-calculator': '/tools/planning/data-breach-cost-calculator',
  'roi-calculator': '/tools/planning/cybersecurity-roi-calculator',
  'risk-matrix': '/tools/planning/risk-matrix-calculator',
  'maturity-assessment': '/tools/planning/cybersecurity-maturity-assessment',
  'business-case-builder': '/tools/business-case-builder',
  'security-budget-calculator': '/tools/planning/cybersecurity-budget-calculator',

  // Security tools
  'hash-generator': '/tools/security/hash-generator',
  'hash-lookup': '/tools/security/hash-lookup',
  'ioc-extractor': '/tools/security/ioc-extractor',
  'threat-intel': '/tools/security/threat-intel-aggregator',
  'cve-lookup': '/tools/security/cve-lookup',
  'cwe-lookup': '/tools/security/cwe-lookup',
  'cvss-calculator': '/tools/security/cvss-calculator',
  'password-strength': '/tools/security/password-strength-checker',

  // Network tools
  'whois': '/tools/network/whois-lookup',
  'dns': '/tools/network/dns-lookup',
  'ip-geo': '/tools/network/ip-geolocation-lookup',
  'bgp-as': '/tools/network/bgp-as-lookup',
  'ip-risk': '/tools/security/ip-risk-checker',
  'domain-risk': '/tools/security/domain-spoofing-detector',

  // Email tools
  'spf-generator': '/tools/security/spf-generator',
  'dkim-generator': '/tools/security/dmarc-generator', // DKIM is part of DMARC generator
  'dmarc-generator': '/tools/security/dmarc-generator',
  'mx-checker': '/tools/security/email-validator-mx-checker',
  'email-headers': '/tools/security/email-header-analyzer',
  'email-validator': '/tools/security/email-validator-mx-checker',

  // Developer tools - Data Format
  'json-formatter': '/tools/developer/json-formatter',
  'json-validator': '/tools/developer/json-validator',
  'data-format-converter': '/tools/developer/data-format-converter',
  'csv-json': '/tools/developer/csv-json-converter',
  'yaml-json': '/tools/developer/yaml-json-converter',
  'excel-json': '/tools/developer/excel-to-json-converter',
  'diff-checker': '/tools/developer/diff-checker',

  // Developer tools - Encoding
  'base64': '/tools/security/base64-encoder-decoder',
  'url-encoder': '/tools/developer/url-encoder',
  'html-encoder': '/tools/developer/html-encoder',
  'jwt-decoder': '/tools/security/jwt-decoder',

  // Developer tools - Text/String
  'regex-tester': '/tools/developer/regex-tester',
  'string-extractor': '/tools/security/string-extractor',
  'word-counter': '/tools/developer/word-counter',
  'lorem-ipsum': '/tools/developer/lorem-ipsum-generator',
  'markdown-preview': '/tools/developer/markdown-preview',

  // Developer tools - Code Generation
  'color-picker': '/tools/developer/color-picker',
  'sri-generator': '/tools/developer/sri-hash-generator',
  'uuid-generator': '/tools/developer/uuid-generator',
  'mock-data': '/tools/developer/mock-data-generator',
  'qr-code': '/tools/media/qr-code-generator',
  'gitignore': '/tools/developer/gitignore-generator',
  'dockerfile': '/tools/developer/dockerfile-generator',
  'docker-command': '/tools/developer/docker-command-builder',
  'env-generator': '/tools/developer/env-file-generator',

  // Developer tools - Time
  'cron-builder': '/tools/developer/cron-builder',
  'unix-timestamp': '/tools/developer/unix-timestamp-converter',
  'time-duration': '/tools/planning/time-duration-calculator',

  // Developer tools - SQL
  'sql-formatter': '/tools/developer/sql-formatter',
  'sql-converter': '/tools/developer/sql-converter',

  // Developer tools - HTTP/API
  'http-builder': '/tools/developer/http-request-builder',
  'user-agent': '/tools/developer/user-agent-parser',
  'http-status': '/tools/developer/http-status-codes',
  'webhook-payload': '/tools/developer/webhook-payload-generator',
  'webhook-tester': '/tools/developer/webhook-tester-inspector',

  // Developer tools - Container/K8s
  'k8s-validator': '/tools/security/kubernetes-manifest-validator',

  // Cipher tools
  'cipher-identifier': '/tools/security/cipher-identifier',
  'caesar-cipher': '/tools/security/caesar-cipher',
  'rot13-cipher': '/tools/security/rot13-cipher',
  'vigenere-cipher': '/tools/security/vigenere-cipher',
  'substitution-cipher': '/tools/security/substitution-cipher',
  'xor-cipher': '/tools/security/xor-cipher',
  'aes-encryption': '/tools/security/aes-encryption-tool',
  'affine-cipher': '/tools/security/affine-cipher',
  'rail-fence-cipher': '/tools/security/rail-fence-cipher',
  'atbash-cipher': '/tools/security/atbash-cipher',
  'playfair-cipher': '/tools/security/playfair-cipher',
  'beaufort-cipher': '/tools/security/beaufort-cipher',
  'hill-cipher': '/tools/security/hill-cipher',
  'enigma-simulator': '/tools/security/enigma-simulator',
  'one-time-pad': '/tools/security/one-time-pad',
  'encoding-chain': '/tools/security/encoding-chain-analyzer',

  // Certificate tools
  'x509-decoder': '/tools/security/x509-decoder',
  'ssl-checker': '/tools/security/ssl-checker',
  'csr-generator': '/tools/security/certificate-csr-generator',
  'chain-builder': '/tools/security/certificate-chain-builder',
  'ct-lookup': '/tools/security/certificate-transparency-lookup',
  'ocsp-checker': '/tools/security/ocsp-crl-checker',
  'ssh-keygen': '/tools/security/ssh-key-generator',
  'pgp-keygen': '/tools/security/pgp-key-generator',

  // Public key education tools
  'rsa-playground': '/tools/security/rsa-playground',
  'public-key-concepts': '/tools/security/public-key-concepts',
} as const;

export type ToolKey = keyof typeof TOOL_PATHS;

/**
 * Build a link to another tool with state
 */
export function buildToolLink(
  tool: ToolKey | string,
  state?: URLStateObject,
  options?: { source?: string }
): string {
  const path = tool in TOOL_PATHS
    ? TOOL_PATHS[tool as ToolKey]
    : tool;

  if (!state && !options?.source) {
    return path;
  }

  const fullState: URLStateObject = {
    ...state,
    ...(options?.source && { source: options.source }),
  };

  const params = encodeState(fullState);
  const queryString = params.toString();
  return queryString ? `${path}?${queryString}` : path;
}

/**
 * Navigate to another tool with state
 */
export function navigateToTool(
  tool: ToolKey | string,
  state?: URLStateObject,
  options?: { source?: string; newTab?: boolean }
): void {
  if (typeof window === 'undefined') return;

  const url = buildToolLink(tool, state, { source: options?.source });

  if (options?.newTab) {
    window.open(url, '_blank');
  } else {
    window.location.href = url;
  }
}

/**
 * Get the source tool that navigated to current tool
 */
export function getSourceTool(): string | null {
  return getSearchParams().get('source');
}

/**
 * Check if current page was navigated from another tool
 */
export function hasSourceTool(): boolean {
  return getSourceTool() !== null;
}

/**
 * Get display name for a tool key
 */
export function getToolDisplayName(tool: ToolKey | string): string {
  const names: Record<string, string> = {
    // Planning tools
    'budget-calculator': 'Budget Calculator',
    'breach-cost-calculator': 'Breach Cost Calculator',
    'roi-calculator': 'ROI Calculator',
    'risk-matrix': 'Risk Matrix',
    'maturity-assessment': 'Maturity Assessment',

    // Security tools
    'hash-generator': 'Hash Generator',
    'hash-lookup': 'Hash Lookup',
    'ioc-extractor': 'IOC Extractor',
    'threat-intel': 'Threat Intel Aggregator',
    'cve-lookup': 'CVE Lookup',
    'cwe-lookup': 'CWE Lookup',
    'cvss-calculator': 'CVSS Calculator',
    'password-strength': 'Password Strength Checker',

    // Network tools
    'whois': 'WHOIS Lookup',
    'dns': 'DNS Lookup',
    'ip-geo': 'IP Geolocation',
    'bgp-as': 'BGP AS Lookup',
    'ip-risk': 'IP Risk Checker',
    'domain-risk': 'Domain Spoofing Detector',

    // Email tools
    'spf-generator': 'SPF Generator',
    'dkim-generator': 'DKIM Generator',
    'dmarc-generator': 'DMARC Generator',
    'mx-checker': 'MX Checker',
    'email-headers': 'Email Header Analyzer',
    'email-validator': 'Email Validator',
    'email-setup-wizard': 'Email Setup Wizard',

    // Developer tools - Data Format
    'json-formatter': 'JSON Formatter',
    'json-validator': 'JSON Validator',
    'data-format-converter': 'Data Format Converter',
    'csv-json': 'CSV to JSON',
    'yaml-json': 'YAML to JSON',
    'excel-json': 'Excel to JSON',
    'diff-checker': 'Diff Checker',

    // Developer tools - Encoding
    'base64': 'Base64 Encoder/Decoder',
    'url-encoder': 'URL Encoder',
    'html-encoder': 'HTML Encoder',
    'jwt-decoder': 'JWT Decoder',

    // Developer tools - Text/String
    'regex-tester': 'Regex Tester',
    'string-extractor': 'String Extractor',
    'word-counter': 'Word Counter',
    'lorem-ipsum': 'Lorem Ipsum Generator',
    'markdown-preview': 'Markdown Preview',

    // Developer tools - Code Generation
    'color-picker': 'Color Picker',
    'sri-generator': 'SRI Hash Generator',
    'uuid-generator': 'UUID Generator',
    'mock-data': 'Mock Data Generator',
    'qr-code': 'QR Code Generator',
    'gitignore': 'Gitignore Generator',
    'dockerfile': 'Dockerfile Generator',
    'docker-command': 'Docker Command Builder',
    'env-generator': 'Env File Generator',

    // Developer tools - Time
    'cron-builder': 'Cron Builder',
    'unix-timestamp': 'Unix Timestamp Converter',
    'time-duration': 'Time Duration Calculator',

    // Developer tools - SQL
    'sql-formatter': 'SQL Formatter',
    'sql-converter': 'SQL Converter',

    // Developer tools - HTTP/API
    'http-builder': 'HTTP Request Builder',
    'user-agent': 'User Agent Parser',
    'http-status': 'HTTP Status Codes',
    'webhook-payload': 'Webhook Payload Generator',
    'webhook-tester': 'Webhook Tester',

    // Developer tools - Container/K8s
    'k8s-validator': 'Kubernetes Validator',

    // Cipher tools
    'cipher-identifier': 'Cipher Identifier',
    'caesar-cipher': 'Caesar Cipher',
    'rot13-cipher': 'ROT13 Cipher',
    'vigenere-cipher': 'Vigenère Cipher',
    'substitution-cipher': 'Substitution Cipher',
    'xor-cipher': 'XOR Cipher',
    'aes-encryption': 'AES Encryption',
    'affine-cipher': 'Affine Cipher',
    'rail-fence-cipher': 'Rail Fence Cipher',
    'atbash-cipher': 'Atbash Cipher',
    'playfair-cipher': 'Playfair Cipher',
    'beaufort-cipher': 'Beaufort Cipher',
    'hill-cipher': 'Hill Cipher',
    'enigma-simulator': 'Enigma Simulator',
    'one-time-pad': 'One-Time Pad',
    'encoding-chain': 'Encoding Chain Analyzer',

    // Certificate tools
    'x509-decoder': 'X.509 Decoder',
    'ssl-checker': 'SSL Checker',
    'csr-generator': 'CSR Generator',
    'chain-builder': 'Certificate Chain Builder',
    'ct-lookup': 'CT Log Lookup',
    'ocsp-checker': 'OCSP/CRL Checker',
    'ssh-keygen': 'SSH Key Generator',
    'pgp-keygen': 'PGP Key Generator',

    // Public key education tools
    'rsa-playground': 'RSA Playground',
    'public-key-concepts': 'Public Key Concepts',
  };
  return names[tool] || tool;
}

/**
 * Get related tools for a given tool (for "Send to" buttons)
 */
export function getRelatedTools(tool: ToolKey): ToolKey[] {
  const relations: Record<ToolKey, ToolKey[]> = {
    // Planning tool chain
    'budget-calculator': ['breach-cost-calculator', 'roi-calculator', 'risk-matrix'],
    'breach-cost-calculator': ['roi-calculator', 'budget-calculator', 'risk-matrix'],
    'roi-calculator': ['budget-calculator', 'breach-cost-calculator'],
    'risk-matrix': ['breach-cost-calculator', 'budget-calculator'],
    'maturity-assessment': ['budget-calculator', 'risk-matrix'],
    'business-case-builder': ['budget-calculator', 'breach-cost-calculator', 'roi-calculator', 'risk-matrix'],
    'security-budget-calculator': ['breach-cost-calculator', 'roi-calculator', 'risk-matrix'],

    // Security tool chain
    'hash-generator': ['hash-lookup', 'ioc-extractor'],
    'hash-lookup': ['ioc-extractor', 'threat-intel'],
    'ioc-extractor': ['threat-intel', 'ip-risk', 'domain-risk'],
    'threat-intel': ['ioc-extractor', 'cve-lookup'],
    'cve-lookup': ['cwe-lookup', 'cvss-calculator'],
    'cwe-lookup': ['cve-lookup'],
    'cvss-calculator': ['cve-lookup', 'risk-matrix'],
    'password-strength': [],

    // Network tool chain
    'whois': ['dns', 'ip-geo', 'domain-risk'],
    'dns': ['whois', 'ip-geo', 'mx-checker'],
    'ip-geo': ['whois', 'bgp-as', 'ip-risk'],
    'bgp-as': ['ip-geo', 'whois'],
    'ip-risk': ['ip-geo', 'threat-intel'],
    'domain-risk': ['whois', 'dns', 'threat-intel'],

    // Email tool chain
    'spf-generator': ['dkim-generator', 'dmarc-generator', 'mx-checker'],
    'dkim-generator': ['spf-generator', 'dmarc-generator'],
    'dmarc-generator': ['spf-generator', 'dkim-generator'],
    'mx-checker': ['dns', 'spf-generator', 'email-headers'],
    'email-headers': ['mx-checker', 'spf-generator'],
    'email-validator': ['mx-checker', 'dns'],

    // Developer tools - Data Format
    'json-formatter': ['base64', 'json-validator', 'data-format-converter', 'diff-checker', 'url-encoder'],
    'json-validator': ['json-formatter', 'data-format-converter'],
    'data-format-converter': ['json-formatter', 'json-validator', 'csv-json', 'yaml-json'],
    'csv-json': ['json-formatter', 'json-validator', 'data-format-converter'],
    'yaml-json': ['json-formatter', 'json-validator', 'data-format-converter'],
    'excel-json': ['json-formatter', 'json-validator'],
    'diff-checker': ['json-formatter', 'hash-generator'],

    // Developer tools - Encoding
    'base64': ['url-encoder', 'json-formatter', 'xor-cipher', 'encoding-chain', 'html-encoder'],
    'url-encoder': ['base64', 'html-encoder', 'hash-generator'],
    'html-encoder': ['url-encoder', 'base64'],
    'jwt-decoder': ['json-formatter', 'base64', 'hash-generator'],

    // Developer tools - Text/String
    'regex-tester': ['string-extractor', 'base64', 'hash-generator'],
    'string-extractor': ['regex-tester', 'hash-generator', 'word-counter'],
    'word-counter': ['lorem-ipsum', 'hash-generator'],
    'lorem-ipsum': ['word-counter', 'markdown-preview'],
    'markdown-preview': ['lorem-ipsum'],

    // Developer tools - Code Generation
    'color-picker': [],
    'sri-generator': ['hash-generator'],
    'uuid-generator': ['mock-data', 'hash-generator', 'qr-code'],
    'mock-data': ['json-formatter', 'json-validator', 'uuid-generator'],
    'qr-code': ['uuid-generator'],
    'gitignore': ['env-generator'],
    'dockerfile': ['docker-command', 'k8s-validator'],
    'docker-command': ['dockerfile'],
    'env-generator': ['gitignore'],

    // Developer tools - Time
    'cron-builder': ['unix-timestamp', 'time-duration'],
    'unix-timestamp': ['cron-builder', 'time-duration'],
    'time-duration': ['unix-timestamp', 'cron-builder'],

    // Developer tools - SQL
    'sql-formatter': ['sql-converter', 'diff-checker'],
    'sql-converter': ['sql-formatter', 'diff-checker'],

    // Developer tools - HTTP/API
    'http-builder': ['user-agent', 'json-formatter', 'url-encoder'],
    'user-agent': ['http-builder'],
    'http-status': [],
    'webhook-payload': ['json-formatter', 'json-validator', 'webhook-tester'],
    'webhook-tester': ['webhook-payload', 'json-formatter'],

    // Developer tools - Container/K8s
    'k8s-validator': ['yaml-json', 'json-formatter', 'dockerfile'],

    // Cipher tools
    'cipher-identifier': ['caesar-cipher', 'vigenere-cipher', 'substitution-cipher', 'xor-cipher'],
    'caesar-cipher': ['cipher-identifier', 'vigenere-cipher', 'rot13-cipher', 'affine-cipher'],
    'rot13-cipher': ['caesar-cipher', 'cipher-identifier'],
    'vigenere-cipher': ['cipher-identifier', 'caesar-cipher', 'beaufort-cipher', 'substitution-cipher', 'playfair-cipher'],
    'substitution-cipher': ['cipher-identifier', 'caesar-cipher', 'vigenere-cipher'],
    'xor-cipher': ['cipher-identifier', 'aes-encryption', 'one-time-pad', 'base64'],
    'aes-encryption': ['xor-cipher', 'hash-generator', 'base64'],
    'affine-cipher': ['cipher-identifier', 'caesar-cipher'],
    'rail-fence-cipher': ['cipher-identifier'],
    'atbash-cipher': ['cipher-identifier', 'caesar-cipher', 'rot13-cipher'],
    'playfair-cipher': ['cipher-identifier', 'vigenere-cipher', 'hill-cipher'],
    'beaufort-cipher': ['vigenere-cipher', 'cipher-identifier', 'caesar-cipher', 'affine-cipher'],
    'hill-cipher': ['cipher-identifier', 'playfair-cipher', 'affine-cipher', 'vigenere-cipher'],
    'enigma-simulator': ['cipher-identifier', 'vigenere-cipher', 'substitution-cipher', 'caesar-cipher'],
    'one-time-pad': ['xor-cipher', 'aes-encryption'],
    'encoding-chain': ['base64', 'xor-cipher', 'cipher-identifier'],

    // Certificate tools
    'x509-decoder': ['ssl-checker', 'csr-generator', 'ct-lookup', 'chain-builder', 'ocsp-checker'],
    'ssl-checker': ['x509-decoder', 'ct-lookup', 'ocsp-checker', 'dns', 'whois'],
    'csr-generator': ['x509-decoder', 'ssl-checker'],
    'chain-builder': ['x509-decoder', 'ssl-checker', 'ocsp-checker'],
    'ct-lookup': ['x509-decoder', 'ssl-checker', 'dns'],
    'ocsp-checker': ['x509-decoder', 'ssl-checker', 'chain-builder'],
    'ssh-keygen': ['pgp-keygen', 'rsa-playground'],
    'pgp-keygen': ['ssh-keygen', 'rsa-playground'],

    // Public key education tools
    'rsa-playground': ['public-key-concepts', 'ssh-keygen', 'pgp-keygen', 'aes-encryption'],
    'public-key-concepts': ['rsa-playground', 'ssh-keygen', 'pgp-keygen', 'hash-generator'],
  };

  return relations[tool] || [];
}
